/* organization/OrgAnalytics — Dedicated analytics page for the org.
   All heavy charts live here. Dashboard stays operational-only.
   RBAC: org.analytics.view capability required. */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { PageHead, StatCard, MiniBar, ProgressBar } from '../platform/_ui';
import { pct, orgLicenses, orgWorkspaces, orgMembers, PLAN_TONES } from '../platform/platformStore';
import { getOrgGrowth, getOrgOverview, getOrgWorkspaceActivity, getOrgMemberActivity, getOrgDeals, getOrgStorageTrend } from '../api/platform.api.js';
import { exportCSV } from '../platform/utils/exportUtils.js';
import { usePermissions } from './auth/usePermissions.js';
import { DeniedState } from './auth/DeniedState.jsx';
import { ORG } from './auth/capabilities.js';

const DATE_OPTS = ['Today', 'Last 7 Days', 'Last 30 Days', 'Last Quarter', 'This Year', 'All Time'];

/* Chart colors — hex values needed for SVG/canvas mark rendering */
const C = {
  blue:   'var(--nx-accent, #125AB0)',
  purple: '#6E3FBA',
  amber:  '#E08A00',
  green:  '#0E8C5A',
};

const MONTH_LABELS = Array.from({ length: 12 }, (_, i) => {
  const d = new Date();
  d.setMonth(d.getMonth() - (11 - i));
  return d.toLocaleString('default', { month: 'short', year: '2-digit' });
});

/* Threshold-based color selector — extracted to module scope so branches
   don't count toward component cognitive complexity (SonarJS S3776). */
const utilColor = (val, hi, lo, base) => {
  if (val >= 90) return hi;
  if (val >= 70) return lo;
  return base;
};

const fmtCurrency = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
};

const toDateParams = (range) => {
  const to   = new Date();
  const from = new Date(to);
  switch (range) {
    case 'Today':        from.setHours(0, 0, 0, 0); break;
    case 'Last 7 Days':  from.setDate(to.getDate() - 7); break;
    case 'Last 30 Days': from.setDate(to.getDate() - 30); break;
    case 'Last Quarter': from.setMonth(to.getMonth() - 3); break;
    case 'This Year':    from.setMonth(0); from.setDate(1); from.setHours(0, 0, 0, 0); break;
    default:             from.setFullYear(to.getFullYear() - 1); break;
  }
  return { from: from.toISOString(), to: to.toISOString() };
};

export default function OrgAnalytics({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const toast      = useToast();

  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel');
  const perm       = usePermissions();

  const org         = data.state.organizations.find(o => o.id === orgId);
  const lics        = orgLicenses(data.state, orgId);
  const userLic     = lics.find(l => l.type === 'USER');
  const licAllotted = userLic?.purchased ?? 0;
  const licUsed     = userLic?.used      ?? org?.users ?? 0;
  const wss         = orgWorkspaces(data.state, orgId);
  const members     = orgMembers(data.state, orgId);

  const [dateRange,            setDateRange]            = useState('Last 30 Days');
  const [loading,              setLoading]              = useState(true);
  const [refreshKey,           setRefreshKey]           = useState(0);
  const [userPoints,           setUserPoints]           = useState([]);
  const [overviewData,         setOverviewData]         = useState(null);
  const [wsActivityPoints,     setWsActivityPoints]     = useState([]);
  const [memberActivityPoints, setMemberActivityPoints] = useState([]);
  const [dealsData,            setDealsData]            = useState(null);
  const [storagePoints,        setStoragePoints]        = useState([]);

  /* licUtilPct derived before early returns so it's stable for the RBAC check render */
  const licUtilPct = useMemo(() => pct(licUsed, licAllotted), [licUsed, licAllotted]);

  useEffect(() => {
    if (!orgId || !org) return;
    setLoading(true);
    let failed = false;
    const dp = toDateParams(dateRange);
    Promise.all([
      getOrgGrowth(orgId, dp)
        .then(res => setUserPoints(res?.points?.map(p => p.count) ?? []))
        .catch(() => { setUserPoints([]); failed = true; }),
      getOrgOverview(orgId)
        .then(res => setOverviewData(res))
        .catch(() => { setOverviewData(null); failed = true; }),
      getOrgWorkspaceActivity(orgId, dp)
        .then(res => setWsActivityPoints(res?.points ?? []))
        .catch(() => { setWsActivityPoints([]); failed = true; }),
      getOrgMemberActivity(orgId, dp)
        .then(res => setMemberActivityPoints(res?.points ?? []))
        .catch(() => { setMemberActivityPoints([]); failed = true; }),
      getOrgDeals(orgId, dp)
        .then(res => setDealsData(res))
        .catch(() => { setDealsData(null); failed = true; }),
      getOrgStorageTrend(orgId, dp)
        .then(res => setStoragePoints(res?.points ?? []))
        .catch(() => { setStoragePoints([]); failed = true; }),
    ]).then(() => {
      if (failed) toast({ tone: 'warning', icon: 'AlertTriangle', title: 'Some analytics data could not be loaded' });
    }).finally(() => setLoading(false));
  }, [orgId, dateRange, refreshKey]);

  const activeWs  = wss.filter(w => (w.status || '').toUpperCase() === 'ACTIVE').length;
  const storageGb = overviewData?.storageGb    ?? 0;
  const openDeals = overviewData?.openDeals    ?? null;
  const wonValue  = overviewData?.wonValue     ?? null;
  const pipeValue = overviewData?.pipelineValue ?? null;

  if (!org) return null;

  if (!perm.has(ORG.ANALYTICS_VIEW)) {
    return (
      <div className="nx-cc-page">
        <PageHead title="Analytics" sub={org.name} />
        <DeniedState
          reason="insufficient_permission"
          message="Analytics requires the org.analytics.view capability. Contact your org admin to request access."
          action={() => navigate(`/organization/${orgId}`)}
          actionLabel="Back to Dashboard"
        />
      </div>
    );
  }

  const licUtilTextColor = utilColor(licUtilPct, 'var(--nx-danger)', 'var(--nx-warning)', 'var(--nx-text-muted)');
  const licBarColor      = utilColor(licUtilPct, 'var(--nx-danger)', 'var(--nx-warning)', 'var(--nx-accent)');

  const topWs = [...wss].sort((a, b) => (b.members || 0) - (a.members || 0)).slice(0, 5);
  const topMembers = [...members]
    .sort((a, b) => {
      if (a.createdAt && b.createdAt) return new Date(b.createdAt) - new Date(a.createdAt);
      return (a.name || '').localeCompare(b.name || '');
    })
    .slice(0, 5);

  const handleExport = () => {
    const rows = [
      { metric: 'Total Members',       value: org.users || members.length },
      { metric: 'Active Workspaces',   value: activeWs },
      { metric: 'License Utilization', value: licUtilPct + '%' },
      { metric: 'License Seats',       value: `${licUsed} / ${licAllotted}` },
      { metric: 'Storage Used (GB)',   value: storageGb || '—' },
      { metric: 'Open Deals',          value: openDeals ?? '—' },
      { metric: 'Won Value',           value: fmtCurrency(wonValue) },
      { metric: 'Pipeline Value',      value: fmtCurrency(pipeValue) },
      { metric: 'Date Range',          value: dateRange },
    ];
    exportCSV('org-analytics-' + (org.name || '').replace(/\s+/g, '-').toLowerCase(), rows, ['metric', 'value']);
    toast({ tone: 'success', icon: 'Download', title: 'Analytics exported' });
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Analytics"
        sub={[org.name, org.plan ? org.plan + ' plan' : null].filter(Boolean).join(' · ')}
        back={returnTo ? { label: returnLabel ? `Back to ${returnLabel}` : 'Back', onClick: () => navigate(returnTo) } : undefined}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" icon="RefreshCw" disabled={loading} onClick={() => setRefreshKey(k => k + 1)}>Refresh</Btn>
            <Btn variant="ghost" icon="Download" onClick={handleExport}>Export CSV</Btn>
          </div>
        }
      />

      {/* Date filter — range selection for future date-aware endpoints */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {DATE_OPTS.map(d => (
          <button key={d} onClick={() => setDateRange(d)}
            style={{
              padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', border: '1.5px solid',
              background:  dateRange === d ? 'var(--nx-accent, #125AB0)' : 'transparent',
              color:       dateRange === d ? '#fff' : 'var(--nx-text-muted)',
              borderColor: dateRange === d ? 'var(--nx-accent, #125AB0)' : 'var(--nx-border)',
              transition: 'all 0.15s',
            }}>
            {d}
          </button>
        ))}
        {loading && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--nx-text-muted)' }}>
            <NexusIcon name="Loader" size={12} /> Refreshing…
          </span>
        )}
      </div>

      {/* Summary KPIs — plan already in PageHead subtitle; 5 metric cards */}
      <div className="nx-cc-stat-grid nx-kpi-5">
        <StatCard label="Total Members"       value={org.users || members.length}             icon="Users"      color="blue"   />
        <StatCard label="Active Workspaces"   value={activeWs}                                icon="LayoutGrid" color="blue"   />
        <StatCard label="License Utilization" value={licUtilPct + '%'} sub={licUsed + '/' + licAllotted + ' seats'} icon="Key" color="purple" />
        <StatCard label="Storage Used"        value={storageGb ? storageGb + ' GB' : '—'}     icon="HardDrive"  color="amber"  />
        <StatCard label="Open Deals"          value={openDeals ?? 0}                          icon="Briefcase"  color="amber"  />
      </div>

      {/* Row 1 — User Growth + Workspace Activity */}
      <div className="nx-cc-grid2" style={{ marginBottom: 16 }}>
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title">User Growth</span>
            <Pill tone={PLAN_TONES[org.plan] || 'gray'}>{dateRange}</Pill>
          </div>
          <div style={{ padding: '12px 0 4px' }}>
            {userPoints.length > 0
              ? <MiniBar points={userPoints} color={C.blue} label="Members (12 months)" current={(org.users || members.length) + ' members'} labels={MONTH_LABELS} />
              : <div className="nx-empty" style={{ padding: '16px 0' }}><span className="nx-muted nx-mini">No data for this period.</span></div>
            }
          </div>
        </div>
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title">Workspace Activity</span>
            <Pill tone="blue">{wss.length} total</Pill>
          </div>
          <div style={{ padding: '12px 0 4px' }}>
            {wsActivityPoints.some(p => p.count > 0)
              ? <MiniBar points={wsActivityPoints.map(p => p.count)} color={C.purple} label="Workspace memberships" current={wss.length + ' workspaces'} labels={wsActivityPoints.map(p => p.label)} />
              : <div className="nx-empty" style={{ padding: '16px 0' }}><span className="nx-muted nx-mini">No workspace activity for this period.</span></div>
            }
          </div>
        </div>
      </div>

      {/* Row 2 — Member Activity + License Usage */}
      <div className="nx-cc-grid2" style={{ marginBottom: 16 }}>
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title">Member Activity</span>
            <Pill tone="green">{members.length} members</Pill>
          </div>
          <div style={{ padding: '12px 0 4px' }}>
            {memberActivityPoints.some(p => p.count > 0)
              ? <MiniBar points={memberActivityPoints.map(p => p.count)} color={C.green} label="Member joins" current={members.length + ' members'} labels={memberActivityPoints.map(p => p.label)} />
              : <div className="nx-empty" style={{ padding: '16px 0' }}><span className="nx-muted nx-mini">No member activity for this period.</span></div>
            }
          </div>
        </div>
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head"><span className="nx-cc-chart-title">License Usage</span></div>
          <div style={{ padding: '16px 0' }}>
            {licAllotted > 0 ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                  <span style={{ fontWeight: 600 }}>{licUsed} of {licAllotted} seats</span>
                  <span style={{ fontWeight: 700, color: licUtilTextColor }}>
                    {licUtilPct}%
                  </span>
                </div>
                <ProgressBar value={licUsed} max={licAllotted} color={licBarColor} height={8} label="License utilization" />
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 8 }}>
                  {licAllotted - licUsed} seats available
                </div>
              </div>
            ) : (
              <div className="nx-empty" style={{ padding: '16px 0' }}>
                <span className="nx-muted nx-mini">No license data available.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 3 — Storage (C-04: API Usage card removed — no endpoint exists; stubbed UI removed) */}
      <div style={{ marginBottom: 16 }}>
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title">Storage Usage</span>
            <Pill tone="gray">GB</Pill>
          </div>
          <div style={{ padding: '12px 0 4px' }}>
            {storagePoints.some(p => p.gb > 0)
              ? <MiniBar points={storagePoints.map(p => p.gb)} color={C.amber} label="Storage (GB)" current={(storageGb || 0) + ' GB'} labels={storagePoints.map(p => p.label)} />
              : <div className="nx-empty" style={{ padding: '16px 0' }}><NexusIcon name="HardDrive" size={20} /><span className="nx-muted nx-mini">No storage data.</span></div>
            }
          </div>
        </div>
      </div>

      {/* Row 4 — Deal Pipeline + Deal Values */}
      <div className="nx-cc-grid2" style={{ marginBottom: 16 }}>
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title">Deal Pipeline</span>
            <Pill tone="purple">{dateRange}</Pill>
          </div>
          <div style={{ padding: '12px 0 4px' }}>
            {dealsData?.points?.some(p => p.won > 0)
              ? <MiniBar points={dealsData.points.map(p => p.won)} color={C.purple} label="Deals won" current={dealsData.won + ' won'} labels={dealsData.points.map(p => p.label)} />
              : <div className="nx-empty" style={{ padding: '16px 0' }}><NexusIcon name="Briefcase" size={20} /><span className="nx-muted nx-mini">No deal data for this period.</span></div>
            }
          </div>
        </div>
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head"><span className="nx-cc-chart-title">Deal Values</span></div>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Pipeline Value', value: fmtCurrency(pipeValue), color: C.purple, icon: 'TrendingUp'  },
              { label: 'Won Value',      value: fmtCurrency(wonValue),  color: C.green,  icon: 'CheckCircle' },
              { label: 'Open Deals',     value: openDeals != null ? String(openDeals) : '—', color: C.amber, icon: 'Briefcase' },
            ].map(t => (
              <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: t.color + '14', color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <NexusIcon name={t.icon} size={17} />
                </div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--nx-text)', lineHeight: 1.2 }}>{t.value}</div>
                  <div className="nx-muted nx-mini">{t.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 5 — Top Workspaces + Top Active Members */}
      <div className="nx-cc-grid2">
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title">Top Workspaces</span>
            <span className="nx-muted nx-mini">by members</span>
          </div>
          {topWs.length === 0
            ? <div className="nx-empty" style={{ padding: '16px 0' }}><span className="nx-muted nx-mini">No workspaces.</span></div>
            : <div style={{ marginTop: 10 }}>
                {topWs.map((w, i) => {
                  const maxM = topWs[0].members || 1;
                  const barW = Math.round(((w.members || 0) / maxM) * 100);
                  return (
                    <div key={w.id} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                        <span style={{ fontWeight: 600 }}>{i + 1}. {w.name}</span>
                        <span className="nx-muted nx-mini">{w.members || 0} members</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: 'var(--nx-border)' }}>
                        <div style={{ height: 4, borderRadius: 2, width: barW + '%', background: 'var(--nx-accent, #125AB0)' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </div>
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title">Recently Joined Members</span>
            <span className="nx-muted nx-mini">by join date</span>
          </div>
          {topMembers.length === 0
            ? <div className="nx-empty" style={{ padding: '16px 0' }}><span className="nx-muted nx-mini">No members yet.</span></div>
            : <div style={{ marginTop: 8 }}>
                {topMembers.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--nx-border)' }}>
                    <div className="nx-domain-mark" style={{ width: 28, height: 28, fontSize: 11, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', flexShrink: 0 }}>
                      {(m.name || m.email || 'U')[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || m.email}</div>
                      <div className="nx-muted nx-mini">{m.orgRole || 'Member'}</div>
                    </div>
                    <Pill tone={m.status === 'active' ? 'green' : 'gray'}>{m.status ? m.status.charAt(0).toUpperCase() + m.status.slice(1) : 'Active'}</Pill>
                  </div>
                ))}
              </div>
          }
        </div>
      </div>
    </div>
  );
}
